import { Injectable, NgZone } from '@angular/core';
import * as Comlink from 'comlink';
import { Mutex } from 'async-mutex';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TimerClass, TimerType } from '../web-workers/timer.worker';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { TelemetryService } from './telemetry.service';
import { ITimerHandler, NativeTimerFallback } from './native-timer-fallback';

const WORKER_SETUP_RETRY_TIMEOUTS_MS = [3000, 9000, 15000] as const;

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class TimerService {
  // when we solve the worker file changes issue we should make TimerClass implements ITimerHandler
  userTimerClass?: Comlink.Remote<TimerClass> | TimerClass | ITimerHandler;
  private setupMutex = new Mutex();
  private isSetupComplete = false;

  timeoutTimerMap: Map<NodeJS.Timer, Function> = new Map();
  intervalTimerMap: Map<NodeJS.Timer, Function> = new Map();

  private setup25SecondWarningTimeout?: NodeJS.Timer;
  private setup2MinuteWarningTimeout?: NodeJS.Timer;

  constructor(private zone: NgZone, private telemetry: TelemetryService) {}

  async setup(): Promise<void> {
    // Skip mutex if already done
    if (this.isSetupComplete) {
      return;
    }

    await this.setupMutex.runExclusive(async () => {
      // Check again after acquiring lock
      if (this.isSetupComplete) {
        return;
      }

      const setupStartTime = performance.now();
      this.telemetry.logBasicEvent('[timer_worker]', { action: 'setup_start' });
      this.setup25SecondWarningTimeout = this.createSetupPendingWarningTimeout(25);
      this.setup2MinuteWarningTimeout = this.createSetupPendingWarningTimeout(120);

      for (let attempt = 0; attempt < WORKER_SETUP_RETRY_TIMEOUTS_MS.length; attempt++) {
        const timeoutMs = WORKER_SETUP_RETRY_TIMEOUTS_MS[attempt];
        const isLastAttempt = attempt === WORKER_SETUP_RETRY_TIMEOUTS_MS.length - 1;

        try {
          const { worker, remoteTimerWorker } = this.createTimerWorker();
          const setupPromise = this.createWorkerSetupPromise(
            remoteTimerWorker,
            setupStartTime,
            attempt + 1,
          );

          this.userTimerClass = await this.raceAgainstSetupTimeout(setupPromise, worker, timeoutMs);
          this.isSetupComplete = true;
          return;
        } catch (error) {
          this.telemetry.logBasicEvent('[timer_worker]', {
            action: 'setup_retry_failed',
            attempt: attempt + 1,
            timeoutMs,
            message: error instanceof Error ? error.message : String(error),
            isLastAttempt,
          });

          if (isLastAttempt) {
            this.telemetry.logBasicEvent('[timer_worker]', {
              action: 'using_native_fallback',
              setupDurationInMilliseconds: Math.round(performance.now() - setupStartTime),
            });
            this.initNativeFallback();
            return;
          }
        }
      }
    });
  }

  private createTimerWorker(): {
    worker: Worker;
    remoteTimerWorker: Comlink.Remote<typeof TimerClass>;
  } {
    const worker = new Worker(new URL('../web-workers/timer.worker.ts', import.meta.url));

    worker.onerror = (event) => {
      this.telemetry.logBasicEvent('[timer_worker]', {
        action: 'worker_error',
        message: event?.message,
        error: event?.error,
        event: event,
      });
    };

    this.zone.runOutsideAngular(() => {
      worker.onmessage = ({ data }) => {
        this.handleTimerCallback(data.source, data.id);
      };
    });

    return { worker, remoteTimerWorker: Comlink.wrap<typeof TimerClass>(worker) };
  }

  private createWorkerSetupPromise(
    remoteTimerWorker: Comlink.Remote<typeof TimerClass>,
    setupStartTime: number,
    attempt: number,
  ): Promise<Comlink.Remote<TimerClass>> {
    const setupPromise = new remoteTimerWorker();

    void setupPromise.then(() => {
      this.clearSetupPendingWarnings();
      this.telemetry.logBasicEvent('[timer_worker]', {
        action: 'setup_complete',
        setupDurationInMilliseconds: Math.round(performance.now() - setupStartTime),
        attempt,
      });
    });

    return setupPromise;
  }

  private raceAgainstSetupTimeout(
    setupPromise: Promise<Comlink.Remote<TimerClass>>,
    worker: Worker,
    timeoutMs: number,
  ): Promise<Comlink.Remote<TimerClass>> {
    let timeoutId: ReturnType<typeof modifiedSetTimeout>;

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = modifiedSetTimeout(
        () => reject(new Error('Worker setup timed out')),
        timeoutMs,
        true,
      );
    });

    return Promise.race([setupPromise, timeoutPromise])
      .catch((error) => {
        this.telemetry.logBasicEvent('[timer_worker]', {
          action: 'setup_failed',
        });
        worker.terminate();
        throw error;
      })
      .finally(() => clearTimeout(timeoutId));
  }

  private createSetupPendingWarningTimeout(
    delayInSeconds: number,
  ): ReturnType<typeof modifiedSetTimeout> {
    return modifiedSetTimeout(
      () => {
        this.telemetry.logBasicEvent('[timer_worker]', {
          action: 'setup_pending',
          setupPendingThresholdInMilliseconds: delayInSeconds * 1000,
        });
      },
      delayInSeconds * 1000,
      true,
    );
  }

  private handleTimerCallback(type: TimerType, id: NodeJS.Timer): void {
    let cb: Function | undefined;
    switch (type) {
      case TimerType.interval:
        cb = this.intervalTimerMap.get(id);
        break;
      case TimerType.timeout:
        cb = this.timeoutTimerMap.get(id);
        break;
      default:
        break;
    }
    if (cb) {
      cb();
    }
  }

  private initNativeFallback(): void {
    this.userTimerClass = new NativeTimerFallback((type, id) => {
      this.handleTimerCallback(type, id);
    });
    // so that it doesnt try with the worker on upcoming calls
    this.isSetupComplete = true;
  }

  private clearSetupPendingWarnings(): void {
    if (this.setup25SecondWarningTimeout) {
      clearTimeout(this.setup25SecondWarningTimeout);
    }
    if (this.setup2MinuteWarningTimeout) {
      clearTimeout(this.setup2MinuteWarningTimeout);
    }
  }

  async setTimeout(cb: Function, timeoutDurationInMilliseconds: number): Promise<NodeJS.Timer> {
    await this.setup();
    const timeoutId = await this.userTimerClass!.setTimeout(timeoutDurationInMilliseconds);
    this.timeoutTimerMap.set(timeoutId, cb);
    return timeoutId;
  }

  async setInterval(cb: Function, intervalDurationInMilliseconds: number): Promise<NodeJS.Timer> {
    await this.setup();
    const intervalId = await this.userTimerClass!.setInterval(intervalDurationInMilliseconds);
    this.intervalTimerMap.set(intervalId, cb);
    return intervalId;
  }

  async clearTimeout(timeoutId: NodeJS.Timer): Promise<void> {
    if (!this.timeoutTimerMap.has(timeoutId)) {
      return;
    }
    await this.userTimerClass?.clearTimeout(timeoutId);
    this.timeoutTimerMap.delete(timeoutId);
  }

  async clearInterval(intervalId: NodeJS.Timer): Promise<void> {
    if (!this.intervalTimerMap.has(intervalId)) {
      return;
    }
    await this.userTimerClass?.clearInterval(intervalId);
    this.intervalTimerMap.delete(intervalId);
  }
}
